import React, { useRef, useEffect } from 'react';
import './Checkbox.less';
import classNames from 'classnames';
import { DataAttributeProps, DataAttributes } from 'utils/DataAttributeUtils';
import { ValidationWrapper } from './Validation/ValidationWrapper';
import { ValidationSummary } from 'utils/ValidationUtils';
import { useTabbingContext } from 'contexts/TabbingContext';

export type CheckboxSize = 'small' | 'standard' | 'large';

export interface ICheckboxProps extends DataAttributeProps, DataAttributes, React.InputHTMLAttributes<{}> {
  id?: string;
  checked?: boolean;
  label?: string;
  labelClassName?: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  /** override input type, e.g. "radio" */
  type?: string;
  grouped?: boolean;
  indeterminate?: boolean;
  validation?: ValidationSummary;
  checkSize?: number | CheckboxSize;
}

const getClassName = (
  type = 'checkbox',
  grouped = false,
  isDisabled: boolean = false,
  checkSize?: number | CheckboxSize,
  className?: string,
  checked?: boolean
) =>
  classNames(className, {
    'dpc-checkbox': type === 'checkbox',
    'dpc-radio': type === 'radio',
    'dpc-checkbox--grouped': grouped && type !== 'radio',
    'dpc-checkbox--small': checkSize === 'small',
    'dpc-checkbox--large': checkSize === 'large',
    'dpc-checkbox--disabled': isDisabled,
    'dpc-checkbox--selected': checked && type !== 'radio',
    'dpc-radio--selected': checked && type === 'radio',
  });

const getInputClassName = (type = 'checkbox') =>
  classNames({
    'dpc-checkbox__input': type === 'checkbox',
    'dpc-radio__input': type === 'radio',
  });

const getIconClassName = (type = 'checkbox') =>
  classNames({
    'dpc-checkbox__icon': type === 'checkbox',
    'dpc-radio__icon': type === 'radio',
  });

const getLabelClassName = (isSelected?: boolean, type?: string, className?: string) =>
  classNames('dpc-checkbox__text', className, {
    'dpc-checkbox__text--selected': isSelected && type !== 'radio',
    'dpc-radio__text--selected': isSelected && type === 'radio',
  });
/**
 * A checkbox/radio input with accompanying text.
 * Combining the two to standardize styles and anticipate handling label positioning in one place.
 * Also handles indeterminate state.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function Checkbox({
  checked,
  disabled,
  type,
  grouped,
  indeterminate,
  className,
  labelClassName,
  id,
  label,
  name,
  onChange,
  onClick,
  validation,
  value,
  tabIndex: propsTabIndex,
  autoFocus,
  checkSize,
  ...dataAttrs
}: ICheckboxProps) {
  const input = useRef<HTMLInputElement | null>(null);
  const tabIndex = useTabbingContext(propsTabIndex);

  useEffect(() => {
    if (input.current) {
      input.current.indeterminate = !!indeterminate;
    }
  }, [indeterminate]);

  return (
    <ValidationWrapper validation={validation}>
      <label
        className={getClassName(type, grouped, disabled, checkSize, className, checked)}
        htmlFor={id}
        title={label}
        data-label={dataAttrs.dataLabel || dataAttrs['data-label']}
        data-path={dataAttrs.dataPath || dataAttrs['data-path']}
      >
        <input
          type={type || 'checkbox'}
          autoFocus={autoFocus}
          id={id}
          name={name}
          className={getInputClassName(type)}
          value={value}
          onChange={onChange}
          onClick={onClick}
          checked={checked}
          disabled={disabled}
          ref={input}
          tabIndex={tabIndex}
        />
        <i className={getIconClassName(type)} />
        {label && <span className={getLabelClassName(checked, type, labelClassName)}>{label}</span>}
      </label>
    </ValidationWrapper>
  );
}
